import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd, ActivatedRouteSnapshot } from '@angular/router';
import { Analytics, logEvent, setUserProperties } from '@angular/fire/analytics';
import { filter, map, distinctUntilChanged } from 'rxjs/operators';

/**
 * GA4 / Firebase Analytics + Umami for SPA: manual page_view on each route change
 * (automatic measurement only sees the first URL). Use content_section + prev_path for funnels.
 * Custom events are mirrored to Umami for journey tracking in the Umami dashboard.
 *
 * Optional: In GA4 Admin → Data streams → your Web stream → Enhanced measurement,
 * turn off "Page views" if you see duplicate first-page hits (our page_view + automatic).
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly analytics = inject(Analytics);
  private readonly router = inject(Router);

  private initialized = false;
  private prevPath = '';

  /** Call once from AppComponent (e.g. ngOnInit). */
  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map((e) => e.urlAfterRedirects),
        distinctUntilChanged(),
      )
      .subscribe((urlAfterRedirects) => {
        const pagePath = this.normalizePath(urlAfterRedirects);
        const leaf = this.getLeafSnapshot(this.router.routerState.snapshot.root);
        const pageTitle = this.buildPageTitle(leaf, pagePath);
        const contentSection = this.inferContentSection(pagePath);
        const prev = this.prevPath;
        this.prevPath = pagePath;

        const pageLocation = typeof location !== 'undefined' ? location.href : pagePath;

        const pageViewParams = {
          page_location: pageLocation,
          page_path: pagePath,
          page_title: pageTitle,
          content_section: contentSection,
          prev_path: prev || '(entry)',
        };

        logEvent(this.analytics, 'page_view', pageViewParams);
        this.trackUmami('navigation', pageViewParams);
      });
  }

  /** GA4 recommended: user chose an item before navigation (adds funnel steps). */
  trackSelectContent(params: {
    contentType: string;
    itemId: string;
    itemName?: string;
  }): void {
    const payload: Record<string, string> = {
      content_type: params.contentType,
      item_id: params.itemId.slice(0, 100),
    };
    if (params.itemName) {
      payload.item_name = params.itemName.slice(0, 100);
    }
    logEvent(this.analytics, 'select_content', payload);
    this.trackUmami('select_content', payload);
  }

  /**
   * Generic GA4 event (custom names ≤40 chars). Truncate param values to 100 chars.
   * Common events: purchase_intent, outbound_click, ui_interaction.
   */
  logCustom(eventName: string, params: Record<string, string | number> = {}): void {
    const cleaned: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null) {
        continue;
      }
      cleaned[k] = typeof v === 'number' ? v : String(v).slice(0, 100);
    }
    logEvent(this.analytics, eventName, cleaned);
    this.trackUmami(eventName, cleaned);
  }

  /** Clicks that are not purchase/store (likes, modals, newsletter, etc.). */
  trackUiInteraction(action: string, extra: Record<string, string> = {}): void {
    this.logCustom('ui_interaction', { action, ...extra });
  }

  /** Language toggle (custom dimension + user property for segmentation). */
  trackLanguageChange(locale: string): void {
    const safe = locale.slice(0, 10);
    setUserProperties(this.analytics, {
      app_language: safe,
    });
    logEvent(this.analytics, 'language_change', {
      locale: safe,
    });
    this.trackUmami('language_change', { locale: safe });
  }

  /** Mirror events to Umami; ui_interaction uses action as the event name for clearer funnels. */
  private trackUmami(eventName: string, params: Record<string, string | number> = {}): void {
    if (typeof window === 'undefined' || !window.umami?.track) {
      return;
    }

    const data: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) {
        continue;
      }
      data[key] = String(value).slice(0, 100);
    }

    if (eventName === 'ui_interaction' && data.action) {
      const { action, ...rest } = data;
      window.umami.track(action, rest);
      return;
    }

    window.umami.track(eventName, data);
  }

  private normalizePath(url: string): string {
    const path = url.split(/[?#]/)[0] || '/';
    return path.startsWith('/') ? path : `/${path}`;
  }

  private getLeafSnapshot(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    let r = route;
    while (r.firstChild) {
      r = r.firstChild;
    }
    return r;
  }

  private buildPageTitle(leaf: ActivatedRouteSnapshot, pagePath: string): string {
    const data = leaf.data as Record<string, string | undefined>;
    if (data['analyticsTitle']) {
      return data['analyticsTitle'] as string;
    }
    const segments = pagePath.split('/').filter(Boolean);
    if (segments.length === 0) {
      return 'Home';
    }

    if (segments[0] === 'about' && segments.length === 1) {
      return 'About';
    }
    if (segments[0] === 'blog') {
      if (segments.length === 1) {
        return 'Blog';
      }
      if (segments.length === 2 && segments[1] !== 'blog') {
        return `Blog: ${this.slugToLabel(segments[1])}`;
      }
      if (segments[1] === 'blog') {
        if (segments.length === 2) {
          return 'Blog';
        }
        if (segments[2]) {
          return `Blog: ${this.slugToLabel(segments[2])}`;
        }
      }
    }
    if (segments[0] === 'book') {
      if (segments.length === 1) {
        return 'Books';
      }
      if (segments[1] === 'category' && segments[2]) {
        return `Book category: ${this.slugToLabel(segments[2])}`;
      }
      if (segments[1] === 'read' && segments[2]) {
        return `Book read: ${this.slugToLabel(segments[2])}`;
      }
      if (segments[1] === 'history' && segments[2] === 'janMark' && segments[3] === 'story') {
        const id = leaf.params['id'] || segments[4] || '';
        return id ? `Jan Mark story: ${String(id)}` : 'Jan Mark story';
      }
    }
    if (segments[0] === 'illustrations') {
      if (segments.length === 1) {
        return 'Illustrations';
      }
      if (segments[1]) {
        return `Illustrations: ${this.slugToLabel(segments[1])}`;
      }
    }

    return segments.map((s) => this.slugToLabel(s)).join(' › ');
  }

  private inferContentSection(pagePath: string): string {
    const segments = pagePath.split('/').filter(Boolean);
    const first = segments[0] || 'home';

    if (first === 'about' && segments.length === 1) {
      return 'about';
    }
    if (first === 'blog') {
      if (segments.length === 1) {
        return 'blog_list';
      }
      if (segments.length === 2 && segments[1] !== 'blog') {
        return 'blog_post';
      }
      if (segments[1] === 'blog') {
        return segments.length > 2 ? 'blog_post' : 'blog_list';
      }
    }
    if (first === 'book') {
      if (segments.length === 1) {
        return 'book_list';
      }
      if (segments[1] === 'category') {
        return 'book_category';
      }
      if (segments[1] === 'read') {
        return 'book_read';
      }
      if (segments[1] === 'history') {
        return 'book_story';
      }
    }
    if (first === 'illustrations') {
      return segments.length === 1 ? 'illustrations_list' : 'illustrations_category';
    }

    return first;
  }

  private slugToLabel(slug: string): string {
    try {
      return decodeURIComponent(slug).replace(/-/g, ' ');
    } catch {
      return slug.replace(/-/g, ' ');
    }
  }
}
